import React from 'react';
import {
  SolarForecastResponse,
  WindOnshoreForecastResponse,
  WindOffshoreForecastResponse,
  LoadForecastResponse,
  ForecastType,
} from '../../../services/powerService';

interface ForecastSummaryProps {
  forecastData:
    | SolarForecastResponse
    | WindOnshoreForecastResponse
    | WindOffshoreForecastResponse
    | LoadForecastResponse;
  forecastType: ForecastType;
}

const ForecastSummary: React.FC<ForecastSummaryProps> = ({ forecastData, forecastType }) => {
  if (!forecastData || forecastData.data.length === 0) return null;

  // Calculate summary statistics
  const actualValues = forecastData.data
    .map((item) => item.actual_value)
    .filter((val) => val !== null && !isNaN(val)) as number[];
  const dayAheadValues = forecastData.data
    .map((item) => item.day_ahead_forecast)
    .filter((val) => val !== null && !isNaN(val)) as number[];

  // Maximum values
  const maxActual = actualValues.length > 0 ? Math.max(...actualValues) : 0;
  const maxDayAhead = dayAheadValues.length > 0 ? Math.max(...dayAheadValues) : 0;

  // Average values
  const avgActual =
    actualValues.length > 0
      ? actualValues.reduce((sum, val) => sum + val, 0) / actualValues.length
      : 0;
  const avgDayAhead =
    dayAheadValues.length > 0
      ? dayAheadValues.reduce((sum, val) => sum + val, 0) / dayAheadValues.length
      : 0;

  // Calculate forecast accuracy (Mean Absolute Percentage Error)
  const combinedValues = forecastData.data
    .filter((item) => item.actual_value !== null && item.day_ahead_forecast !== null)
    .map((item) => ({
      actual: item.actual_value,
      forecast: item.day_ahead_forecast,
    }));

  const mape =
    combinedValues.length > 0
      ? (combinedValues.reduce((sum, val) => {
          const percentage =
            val.actual !== 0 ? Math.abs((val.actual - val.forecast) / val.actual) : 0;
          return sum + percentage;
        }, 0) /
          combinedValues.length) *
        100
      : 0;

  // Get summary title
  let title = '';
  switch (forecastType) {
    case 'solar':
      title = 'Solar Power';
      break;
    case 'wind_onshore':
      title = 'Wind Onshore';
      break;
    case 'wind_offshore':
      title = 'Wind Offshore';
      break;
    case 'load':
      title = 'Load';
      break;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
      <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-300 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Maximum {title}
        </h3>
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {maxActual.toFixed(2)} <span className="text-sm font-normal">MW</span>
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Forecast: {maxDayAhead.toFixed(2)} MW
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-300 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Average {title}
        </h3>
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {avgActual.toFixed(2)} <span className="text-sm font-normal">MW</span>
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Forecast: {avgDayAhead.toFixed(2)} MW
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-300 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Forecast Accuracy
        </h3>
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {isNaN(mape) ? 'N/A' : `${(100 - mape).toFixed(2)}%`}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Based on day-ahead forecasts
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-md p-4 border border-gray-300 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Data Points</h3>
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {forecastData.data.length}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {forecastData.country_code}
          {forecastData.tso ? ` - ${forecastData.tso}` : ''}
        </p>
      </div>
    </div>
  );
};

export default ForecastSummary;
